import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Snackbar from "@mui/material/Snackbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Third Party
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useLazyQuery } from "@apollo/client";

// Internal
import Logo from "../../../assets/logo-primary.png";
import en from "../../../data/en.json";
import { UPSERT_USER } from "../../../data/graphql/mutations";
import { GET_USER_PROFILE_BY_ID } from "../../../data/graphql/queries";
import MobileNavDrawer from "./MobileNavDrawer";
import { buildAccountNav, navigateTo, primaryNav } from "./navItems";

const rightLink = {
  fontSize: 16,
  color: "primary.main",
  mx: 1.5, // Base margin
};

const rightLinkWide = {
  fontSize: 16,
  color: "primary.main",
  mx: 2.5, // Wider margin for items that need more space
};

const rightLinkNarrow = {
  fontSize: 16,
  color: "primary.main",
  mx: 1, // Narrower margin for items that are too far apart
};

const topLevelButton = {
  ...rightLinkWide,
  mr: 4.5,
  color: "secondary.light",
  whiteSpace: "nowrap",
};

const groupButton = {
  ...topLevelButton,
  minHeight: "auto",
  textTransform: "uppercase",
  alignSelf: "center",
};

function AppAppBar() {
  const theme = useTheme();
  // The desktop nav needs ~1110px (logo + six nowrap buttons); below that the
  // buttons overlap the logo, so the drawer covers tablets too.
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [upsertUser, { error: UpsertUserError, data: UpsertUserData }] =
    useMutation(UPSERT_USER);

  const [getUserProfile, { data: userProfileData }] = useLazyQuery(GET_USER_PROFILE_BY_ID);

  const { loginWithRedirect, user } = useAuth0();
  const { logout } = useAuth0();
  const { isAuthenticated, isLoading } = useAuth0();

  const isAdmin = userProfileData?.userProfileById?.role === "admin";

  const [snackbarMessage, setSnackbarMessage] = React.useState<null | string>(
    null
  );
  const [menu, setMenu] = React.useState<null | {
    id: string;
    el: HTMLElement;
  }>(null);
  const [authAnchorEl, setAuthAnchorEl] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleMenuClick = (id: string) => (event: React.MouseEvent<HTMLElement>) => {
    setMenu({ id, el: event.currentTarget });
  };

  const handleMenuClose = () => {
    setMenu(null);
  };

  const handleAuthClick = (event: any) => {
    setAuthAnchorEl(event.currentTarget);
  };

  const handleAuthClose = () => {
    setAuthAnchorEl(null);
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const accountNav = buildAccountNav({ isAuthenticated, isAdmin });

  React.useEffect(() => {
    if (isAuthenticated) {
      upsertUser({
        variables: {
          email: user?.email,
          auth0_id: user?.sub,
          role: "member",
          name: user?.name,
          picture_url: user?.picture,
        },
      });
      getUserProfile({ variables: { user_auth0_id: user?.sub } });
    }
  }, [isAuthenticated, user, upsertUser, getUserProfile]);

  React.useEffect(() => {
    if (UpsertUserData !== undefined) {
      setSnackbarMessage(en.common.loginSuccess);
    }
    if (UpsertUserError !== undefined) {
      setSnackbarMessage(en.common.loginFail);
    }
  }, [UpsertUserData, UpsertUserError]);

  if (isLoading) {
    return (
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
            <Link
              variant="h6"
              underline="none"
              href="/"
              sx={{ pl: 2, fontSize: 24, color: "secondary.light" }}
            >
              {en.common.inkBowl}
            </Link>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <React.Fragment>
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
      />
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              minWidth: { xs: 200, md: 250 },
            }}
          >
            <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
            <Link
              variant="h6"
              underline="none"
              href="/"
              sx={{
                pl: 2,
                fontSize: { xs: 20, md: 24 },
                color: "secondary.light",
              }}
            >
              {en.common.inkBowl}
            </Link>
            {isAdmin && (
              <Button
                onClick={() => window.open("/admin")}
                sx={{
                  ml: 2,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                  fontSize: 14,
                }}
              >
                Admin
              </Button>
            )}
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 650,
              }}
            >
              {primaryNav.map((item) => {
                if (item.kind === "link") {
                  return (
                    <Button
                      key={item.id}
                      onClick={() => navigateTo(item)}
                      sx={topLevelButton}
                    >
                      {item.label}
                    </Button>
                  );
                }

                const menuId = `${item.id}-menu`;

                return (
                  <React.Fragment key={item.id}>
                    <Button
                      aria-controls={menuId}
                      aria-haspopup="true"
                      onClick={handleMenuClick(item.id)}
                      sx={groupButton}
                    >
                      {item.label}
                    </Button>
                    <Menu
                      id={menuId}
                      anchorEl={menu?.el ?? null}
                      keepMounted
                      open={menu?.id === item.id}
                      onClose={handleMenuClose}
                      sx={{ ...rightLinkNarrow }}
                    >
                      {item.children.map((child) => (
                        <MenuItem
                          key={child.id}
                          onClick={() => {
                            handleMenuClose();
                            navigateTo(child);
                          }}
                        >
                          {child.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                );
              })}

              {isAuthenticated && (
                <>
                  <Button
                    aria-controls="auth-menu"
                    aria-haspopup="true"
                    onClick={handleAuthClick}
                    sx={{
                      ...rightLink,
                      color: "secondary.light",
                      whiteSpace: "nowrap",
                      minHeight: "auto",
                      textTransform: "uppercase",
                      alignSelf: "center",
                    }}
                  >
                    {en.common.dashboard}
                  </Button>
                  <Menu
                    id="auth-menu"
                    anchorEl={authAnchorEl}
                    keepMounted
                    open={Boolean(authAnchorEl)}
                    onClose={handleAuthClose}
                  >
                    <MenuItem
                      onClick={() => {
                        handleAuthClose();
                        window.open("/dashboard");
                      }}
                    >
                      {en.common.dashboard}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAuthClose();
                        handleLogout();
                      }}
                    >
                      {en.common.logout}
                    </MenuItem>
                  </Menu>
                </>
              )}

              {!isAuthenticated && (
                <Link
                  variant="h6"
                  component="button"
                  onClick={() => loginWithRedirect()}
                  underline="none"
                  sx={{
                    ...rightLink,
                    color: "secondary.light",
                    whiteSpace: "nowrap",
                  }}
                >
                  {en.common.login}
                </Link>
              )}
            </Box>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={() => setMobileOpen(true)}
                sx={{ color: "secondary.light" }}
              >
                <MenuIcon />
              </IconButton>
              <MobileNavDrawer
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                items={primaryNav}
                accountItems={accountNav}
                isAuthenticated={isAuthenticated}
                onLogin={() => loginWithRedirect()}
                onLogout={handleLogout}
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
    </React.Fragment>
  );
}

export default AppAppBar;
